// src/theme.js
import { createTheme } from "@mui/material/styles";

const tokens = {
  colors: {
    green: "#81c241",
    greenHover: "#6aa832",
    greenLight: "#a3d470",
    darkGreen: "#6aa832",
    white: "#ffffff",
    black: "#000000",
    gray: "#666666",
    mediumGray: "#555555",
    darkGray: "#333333",
    lightGray: "#f9f9f9",
    extraLightGray: "#f4f4f9",
    iconBackground: "#f4f4f9",
    warning: "#666666",
    error: "#333333",
  },
  shadows: {
    subtle: "0 4px 10px rgba(0,0,0,0.2)",
    medium: "0px 8px 20px rgba(0,0,0,0.15)",
    deep: "0 8px 15px rgba(0,0,0,0.2)",
    card: "0px 4px 15px rgba(0,0,0,0.1)",
    dropdown: "0 4px 12px rgba(0,0,0,0.1)",
    icon: "0px 4px 10px rgba(0,0,0,0.1)",
  },
  fonts: {
    primary: "'Orbitron', sans-serif",
    secondary: "'Roboto', sans-serif",
  },
};

const theme = createTheme({
  palette: {
    primary: {
      main: tokens.colors.green,
      light: "#a3d470",
      dark: "#6aa832",
      contrastText: tokens.colors.white,
    },
    secondary: {
      main: tokens.colors.gray,
      light: "#999999",
      dark: tokens.colors.mediumGray,
      contrastText: tokens.colors.white,
    },
    error: {
      main: tokens.colors.darkGray,
      light: tokens.colors.mediumGray,
      dark: tokens.colors.black,
      contrastText: tokens.colors.white,
    },
    warning: {
      main: tokens.colors.gray,
      light: "#999999",
      dark: tokens.colors.mediumGray,
      contrastText: tokens.colors.white,
    },
    info: {
      main: tokens.colors.green,
      light: "#a3d470",
      dark: "#6aa832",
      contrastText: tokens.colors.white,
    },
    success: {
      main: tokens.colors.green,
      light: "#a3d470",
      dark: "#6aa832",
      contrastText: tokens.colors.white,
    },
    background: {
      default: tokens.colors.lightGray,
      paper: tokens.colors.white,
    },
    text: {
      primary: tokens.colors.darkGray,
      secondary: tokens.colors.gray,
    },
    divider: tokens.colors.lightGray,
    action: {
      hover: "rgba(129, 194, 65, 0.08)",
      selected: "rgba(129, 194, 65, 0.16)",
      focus: "rgba(129, 194, 65, 0.12)",
    },
  },
  typography: {
    fontFamily: tokens.fonts.secondary,
    h1: {
      fontFamily: tokens.fonts.primary,
      fontWeight: 700,
    },
    h2: {
      fontFamily: tokens.fonts.primary,
      fontWeight: 600,
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#6aa832",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: tokens.colors.gray,
          "&.Mui-checked": {
            color: tokens.colors.green,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: tokens.colors.gray,
          "&.Mui-checked": {
            color: tokens.colors.green,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: tokens.colors.green,
            "& + .MuiSwitch-track": {
              backgroundColor: tokens.colors.green,
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
              borderColor: tokens.colors.green,
            },
            "&.Mui-focused fieldset": {
              borderColor: tokens.colors.green,
            },
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: tokens.colors.green,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: tokens.colors.green,
          "&:hover": {
            color: "#6aa832",
          },
        },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          "& .MuiPaginationItem-root": {
            "&.Mui-selected": {
              backgroundColor: tokens.colors.green,
              color: tokens.colors.white,
              "&:hover": {
                backgroundColor: "#6aa832",
              },
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            color: tokens.colors.green,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: tokens.colors.green,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: tokens.colors.green,
        },
        colorSuccess: {
          backgroundColor: tokens.colors.green,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: "rgba(129, 194, 65, 0.12)",
          color: tokens.colors.darkGray,
          "& .MuiAlert-icon": {
            color: tokens.colors.green,
          },
        },
        standardError: {
          backgroundColor: "rgba(51, 51, 51, 0.12)",
          color: tokens.colors.darkGray,
          "& .MuiAlert-icon": {
            color: tokens.colors.darkGray,
          },
        },
        standardWarning: {
          backgroundColor: "rgba(102, 102, 102, 0.12)",
          color: tokens.colors.darkGray,
          "& .MuiAlert-icon": {
            color: tokens.colors.gray,
          },
        },
        standardInfo: {
          backgroundColor: "rgba(129, 194, 65, 0.12)",
          color: tokens.colors.darkGray,
          "& .MuiAlert-icon": {
            color: tokens.colors.green,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        barColorPrimary: {
          backgroundColor: tokens.colors.green,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: tokens.colors.green,
        },
      },
    },
  },
});

// ✅ keep old API working
theme.colors = tokens.colors;
theme.shadowsCustom = tokens.shadows; // avoid name clash with MUI shadows
theme.fonts = tokens.fonts;

export default theme;
